import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Select, Button, Modal, Space, Card, Tag, Progress, List, Checkbox } from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined, BookOutlined, CheckCircleOutlined, UpOutlined, DownOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { useLearningPaths, usePosts, useUserProfile } from '../../../hooks/useCommunity';
import communityAPI from '../../../api/community';
import { InlineSpinner } from '../../../componentes/LoadingIndicator';
import { formatNumber } from '../../../utils/numberFormat';
import { getCreationRestrictionCopy } from '../../../utils/restrictionCopy';

const { TextArea } = Input;

// Learning Path View Component
export const LearningPathView = ({ id }) => {
  const { user } = useCommunityAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { learningPaths } = useLearningPaths({}, { paginated: false, pageSize: 200 });
  const [completedLessons, setCompletedLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const learningPath = learningPaths.find(lp => lp.id === parseInt(id));
  const backTo = location.state?.backTo || '/admin/content/learning-paths';
  const fromAdmin = Boolean(location.state?.fromAdmin);

  const handleBack = () => {
    if (fromAdmin) {
      navigate(backTo, { replace: true });
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (user && id) {
      fetchCompletedLessons();
    }
  }, [user, id]);

  const fetchCompletedLessons = async () => {
    try {
      const response = await communityAPI.progress.getCompletedLessons(id);
      setCompletedLessons(response.data.completed_lessons || []);
    } catch (err) {
      console.error('Error fetching completed lessons:', err);
    }
  };

  const toggleLessonComplete = async (postId) => {
    setLoading(true);
    try {
      await communityAPI.progress.toggleComplete(postId, id);
      await fetchCompletedLessons();
    } catch (err) {
      console.error('Error toggling lesson completion:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!learningPath) return <div className="text-center py-10">Learning path not found</div>;

  const completedCount = completedLessons.length;
  const totalCount = learningPath.posts?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'blue';
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="mb-6">
        <div className="mb-4">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-3xl font-bold">{learningPath.title}</h1>
            <div className="flex items-center gap-2">
              {fromAdmin && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                >
                  Back to admin
                </button>
              )}
              <Tag color={getLevelColor(learningPath.level)}>
                {learningPath.level?.toUpperCase()}
              </Tag>
            </div>
          </div>
          
          <div className="flex items-center gap-4 text-gray-500 mb-4">
            <span>By {learningPath.author?.username}</span>
            <span>{formatNumber(totalCount)} lessons</span>
          </div>

          {user && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Progress</span>
                <span>
                  {formatNumber(completedCount)}/{formatNumber(totalCount)} completed
                </span>
              </div>
              <Progress percent={progressPercent} status="active" />
            </div>
          )}
        </div>
        
        <div className="prose max-w-none mb-6">
          <p>{learningPath.description}</p>
        </div>
      </Card>

      {/* Lessons List */}
      <Card title="Lessons">
        <List
          dataSource={learningPath.posts || []}
          renderItem={(post, index) => {
            const isCompleted = completedLessons.includes(post.id);
            return (
              <List.Item
                key={post.id}
                className={`${isCompleted ? 'bg-green-50' : ''} rounded-lg mb-2 p-4`}
                actions={user ? [
                  <Checkbox
                    checked={isCompleted}
                    onChange={() => toggleLessonComplete(post.id)}
                    disabled={loading}
                  >
                    {isCompleted ? 'Completed' : 'Mark Complete'}
                  </Checkbox>
                ] : []}
              >
                <List.Item.Meta
                  avatar={
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                      {isCompleted ? (
                        <CheckCircleOutlined className="text-green-500" />
                      ) : (
                        <span className="text-blue-600 font-semibold">{index + 1}</span>
                      )}
                    </div>
                  }
                  title={
                    <div className="flex items-center gap-2">
                      <span className={isCompleted ? 'line-through text-gray-500' : ''}>
                        {post.title}
                      </span>
                      {post.category && (
                        <Tag size="small" color="blue">{post.category.name}</Tag>
                      )}
                    </div>
                  }
                  description={
                    <div>
                      <p className="text-gray-600 mb-2">
                        {post.content?.replace(/<[^>]*>/g, '').substring(0, 100)}...
                      </p>
                      <div className="flex gap-2">
                        {post.tags?.map(tag => (
                          <Tag key={tag.id} size="small">{tag.name}</Tag>
                        ))}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
};

// Learning Path Edit Component
export const LearningPathEdit = ({ id, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const { learningPaths, updateLearningPath } = useLearningPaths({}, { paginated: false, pageSize: 200 });
  const { posts } = usePosts({ mine: true, approved_only: true }, { paginated: false, pageSize: 200 });
  const { user } = useCommunityAuth();
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  
  const learningPath = learningPaths.find(lp => lp.id === parseInt(id));
  const canEdit = Boolean(user && learningPath && (user.id === (learningPath.author?.id || learningPath.created_by?.id)));

  useEffect(() => {
    if (learningPath) {
      const initialIds = learningPath.posts?.map(post => post.id) || [];
      form.setFieldsValue({
        title: learningPath.title,
        description: learningPath.description,
        level: learningPath.level,
        post_ids: initialIds
      });
      setSelectedIds(initialIds);
    }
  }, [learningPath, form]);

  useEffect(() => {
    // keep form in sync to submit correctly
    form.setFieldsValue({ post_ids: selectedIds });
  }, [selectedIds, form]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await updateLearningPath(id, { ...values, post_ids: selectedIds });
      onSave?.();
    } catch (err) {
      console.error('Error updating learning path:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!learningPath) return <div className="text-center py-10">Learning path not found</div>;

  if (!canEdit) {
    return (
      <Card className="p-6 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20">
        <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-200 mb-2">Editing Restricted</h3>
        <p className="text-sm text-amber-700/80 dark:text-amber-100">
          Only the author can edit this learning path.
        </p>
        <div className="mt-4">
          <Button onClick={onCancel}>Close</Button>
        </div>
      </Card>
    );
  }

  const selectedPostObjects = selectedIds
    .map(pid => posts.find(p => p.id === pid))
    .filter(Boolean);

  const availablePosts = posts
    .filter(p => !selectedIds.includes(p.id))
    .filter(p => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.title || '').toLowerCase().includes(q) ||
        (p.post_type || '').toLowerCase().includes(q) ||
        (p.category?.name || '').toLowerCase().includes(q)
      );
    });

  const addPost = (id) => {
    if (!selectedIds.includes(id)) setSelectedIds(prev => [...prev, id]);
  };
  const removePost = (id) => setSelectedIds(prev => prev.filter(x => x !== id));
  const moveUp = (index) => {
    if (index <= 0) return;
    setSelectedIds(prev => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };
  const moveDown = (index) => {
    setSelectedIds(prev => {
      if (index >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      return arr;
    });
  };

  return (
    <Form form={form} onFinish={handleSave} layout="vertical" className="[&_.ant-form-item-label>label]:dark:!text-gray-200 [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-select-selector]:dark:!bg-[#0E0E0E] [&_.ant-select-selector]:dark:!border-gray-800 [&_.ant-select-selection-item]:dark:!text-white [&_.ant-card]:dark:!bg-[#161616] [&_.ant-card-head]:dark:!border-gray-800 [&_.ant-card-head-title]:dark:!text-white [&_.ant-card-body]:dark:!text-gray-200 max-w-5xl mx-auto p-6">
      <Form.Item name="title" label="Title" rules={[{ required: true }]}>
        <Input placeholder="Enter learning path title" />
      </Form.Item>

      <Form.Item name="description" label="Description" rules={[{ required: true }]}>
        <TextArea rows={4} placeholder="Enter learning path description" />
      </Form.Item>

      <Form.Item name="level" label="Level" rules={[{ required: true }]}>
        <Select placeholder="Select level">
          <Select.Option value="beginner">Beginner</Select.Option>
          <Select.Option value="intermediate">Intermediate</Select.Option>
          <Select.Option value="advanced">Advanced</Select.Option>
        </Select>
      </Form.Item>

      {/* Keep a hidden field synced for form submission */}
      <Form.Item name="post_ids" hidden>
        <Input type="hidden" />
      </Form.Item>

      <div className="mb-2 font-semibold text-gray-900 dark:text-white">Posts</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={<span className="flex items-center gap-2"><SearchOutlined /> Available Posts</span>} className="h-full">
          <div className="mb-3">
            <Input 
              placeholder="Search by title, type, category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-auto space-y-2">
            {availablePosts.map(p => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary/40 dark:hover:border-primary/40 transition-colors">
                <div className="min-w-0">
                  <div className="font-medium truncate text-gray-900 dark:text-white">{p.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {p.post_type}{p.category?.name ? ` • ${p.category.name}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => addPost(p.id)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary hover:bg-[#229bb8] text-white text-xs font-medium transition-colors shrink-0"
                >
                  <PlusOutlined /> Add
                </button>
              </div>
            ))}
            {availablePosts.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No posts match your search.</div>
            )}
          </div>
        </Card>
        <Card title="Selected (Drag order via arrows)">
          <div className="max-h-80 overflow-auto space-y-2">
            {selectedPostObjects.map((p, idx) => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="min-w-0">
                  <div className="font-medium truncate text-gray-900 dark:text-white">{idx + 1}. {p.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{p.post_type}{p.category?.name ? ` • ${p.category.name}` : ''}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="small" icon={<UpOutlined />} onClick={() => moveUp(idx)} />
                  <Button size="small" icon={<DownOutlined />} onClick={() => moveDown(idx)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removePost(p.id)} />
                </div>
              </div>
            ))}
            {selectedPostObjects.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No posts selected yet.</div>
            )}
          </div>
          {selectedPostObjects.length > 0 && (
            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-500 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Clear All
              </button>
            </div>
          )}
        </Card>
      </div>

      <Form.Item className="mt-6">
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold text-sm transition-colors duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </Form.Item>
    </Form>
  );
};

// Learning Path Create Component (reuses Edit design)
export const LearningPathCreate = ({ onSave, onCancel }) => {
  const [form] = Form.useForm();
  const { createLearningPath } = useLearningPaths({}, { skip: true });
  const { posts } = usePosts({ mine: true, approved_only: true }, { paginated: false, pageSize: 200 });
  const { user } = useCommunityAuth();
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const creationRestriction = useMemo(
    () => getCreationRestrictionCopy(user?.status, 'Learning Path'),
    [user?.status]
  );
  const creationBlocked = Boolean(creationRestriction.blocked);

  useEffect(() => {
    form.setFieldsValue({
      title: '',
      description: '',
      level: 'beginner',
      post_ids: []
    });
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({ post_ids: selectedIds });
  }, [selectedIds, form]);

  const handleCreate = async (values) => {
    if (creationBlocked) {
      return;
    }
    setSaving(true);
    try {
      await createLearningPath({ ...values, post_ids: selectedIds });
      onSave?.();
    } catch (err) {
      console.error('Error creating learning path:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectedPostObjects = selectedIds
    .map(pid => posts.find(p => p.id === pid))
    .filter(Boolean);

  const availablePosts = posts
    .filter(p => !selectedIds.includes(p.id))
    .filter(p => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (p.title || '').toLowerCase().includes(q) ||
        (p.post_type || '').toLowerCase().includes(q) ||
        (p.category?.name || '').toLowerCase().includes(q)
      );
    });

  const addPost = (id) => {
    if (!selectedIds.includes(id)) setSelectedIds(prev => [...prev, id]);
  };
  const removePost = (id) => setSelectedIds(prev => prev.filter(x => x !== id));
  const moveUp = (index) => {
    if (index <= 0) return;
    setSelectedIds(prev => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr;
    });
  };
  const moveDown = (index) => {
    setSelectedIds(prev => {
      if (index >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      return arr;
    });
  };

  if (!user) {
    return (
      <Card className="p-6 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <h3 className="text-lg font-semibold text-red-700 dark:text-red-300 mb-2">Authentication Required</h3>
        <p className="text-sm text-red-600 dark:text-red-400">Please log in to create learning paths.</p>
      </Card>
    );
  }

  if (creationBlocked) {
    return (
      <Card className="p-6 rounded-2xl border border-amber-300/60 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20">
        <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-200 mb-2">{creationRestriction.headline}</h3>
        <p className="text-sm text-amber-700/80 dark:text-amber-100">{creationRestriction.detail}</p>
        <p className="mt-2 text-xs text-amber-600/80 dark:text-amber-200/70">
          {creationRestriction.support || 'Please reach out to a community administrator if you need help reinstating access.'}
        </p>
      </Card>
    );
  }

  return (
    <Form form={form} onFinish={handleCreate} layout="vertical" className="[&_.ant-form-item-label>label]:dark:!text-gray-200 [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-select-selector]:dark:!bg-[#0E0E0E] [&_.ant-select-selector]:dark:!border-gray-800 [&_.ant-select-selection-item]:dark:!text-white [&_.ant-card]:dark:!bg-[#161616] [&_.ant-card-head]:dark:!border-gray-800 [&_.ant-card-head-title]:dark:!text-white [&_.ant-card-body]:dark:!text-gray-200 max-w-5xl mx-auto p-6">
      <Form.Item name="title" label="Title" rules={[{ required: true }]}>
        <Input placeholder="Enter learning path title" />
      </Form.Item>

      <Form.Item name="description" label="Description" rules={[{ required: true }]}>
        <TextArea rows={4} placeholder="Enter learning path description" />
      </Form.Item>

      <Form.Item name="level" label="Level" rules={[{ required: true }]}>
        <Select placeholder="Select level">
          <Select.Option value="beginner">Beginner</Select.Option>
          <Select.Option value="intermediate">Intermediate</Select.Option>
          <Select.Option value="advanced">Advanced</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item name="post_ids" hidden>
        <Input type="hidden" />
      </Form.Item>

      <div className="mb-2 font-semibold text-gray-900 dark:text-white">Posts</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title={<span className="flex items-center gap-2"><SearchOutlined /> Available Posts</span>} className="h-full">
          <div className="mb-3">
            <Input 
              placeholder="Search by title, type, category"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-auto space-y-2">
            {availablePosts.map(p => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary/40 dark:hover:border-primary/40 transition-colors">
                <div className="min-w-0">
                  <div className="font-medium truncate text-gray-900 dark:text-white">{p.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {p.post_type}{p.category?.name ? ` • ${p.category.name}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => addPost(p.id)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary hover:bg-[#229bb8] text-white text-xs font-medium transition-colors shrink-0"
                >
                  <PlusOutlined /> Add
                </button>
              </div>
            ))}
            {availablePosts.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No posts match your search.</div>
            )}
          </div>
        </Card>
        <Card title="Selected (Drag order via arrows)">
          <div className="max-h-80 overflow-auto space-y-2">
            {selectedPostObjects.map((p, idx) => (
              <div key={p.id} className="flex items-center justify-between p-2.5 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="min-w-0">
                  <div className="font-medium truncate text-gray-900 dark:text-white">{idx + 1}. {p.title}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{p.post_type}{p.category?.name ? ` • ${p.category.name}` : ''}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="small" icon={<UpOutlined />} onClick={() => moveUp(idx)} />
                  <Button size="small" icon={<DownOutlined />} onClick={() => moveDown(idx)} />
                  <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removePost(p.id)} />
                </div>
              </div>
            ))}
            {selectedPostObjects.length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">No posts selected yet.</div>
            )}
          </div>
          {selectedPostObjects.length > 0 && (
            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={() => setSelectedIds([])}
                className="px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 text-red-500 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Clear All
              </button>
            </div>
          )}
        </Card>
      </div>

      <Form.Item className="mt-6">
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-primary hover:bg-[#229bb8] text-white font-semibold text-sm transition-colors duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? 'Creating...' : 'Create Learning Path'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
          >
            Cancel
          </button>
        </div>
      </Form.Item>
    </Form>
  );
};

// Learning Path List Component
export const LearningPathList = ({ showActions = true }) => {
  const { learningPaths, loading, deleteLearningPath } = useLearningPaths({}, { paginated: false, pageSize: 200 });
  const { user } = useCommunityAuth();
  const navigate = useNavigate();
  const [editingPath, setEditingPath] = useState(null);

  const handleEdit = (path) => {
    setEditingPath(path);
  };

  const handleDelete = (path) => {
    Modal.confirm({
      title: 'Delete Learning Path',
      content: `Are you sure you want to delete "${path.title}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteLearningPath(path.id)
    });
  };

  const handleView = (path) => {
    navigate(`/community/learning/${path.id}`);
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'blue';
    }
  };

  if (loading) {
    return (
      <div className="py-10 flex justify-center">
        <InlineSpinner tone="slate" message="Loading learning paths..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {learningPaths.map(path => (
        <Card key={path.id} className="hover:shadow-md transition-shadow">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-semibold">{path.title}</h3>
                <Tag color={getLevelColor(path.level)}>
                  {path.level?.toUpperCase()}
                </Tag>
              </div>
              
              <div className="flex items-center gap-4 text-gray-500 mb-3">
                <span>By {path.author?.username}</span>
                <span><BookOutlined /> {path.posts?.length || 0} lessons</span>
              </div>
              
              <p className="text-gray-600 line-clamp-2 mb-3">
                {path.description}
              </p>

              {user && (
                <div className="text-sm text-gray-500">
                  <Progress 
                    percent={0} 
                    size="small" 
                    status="active"
                    format={() => '0% completed'}
                  />
                </div>
              )}
            </div>
            
            {showActions && user && user.id === path.author?.id && (
              <Space>
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  onClick={() => handleView(path)}
                />
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(path)}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(path)}
                />
              </Space>
            )}
          </div>
        </Card>
      ))}

      {/* Edit Modal */}
      <Modal
        className="[&_.ant-modal-content]:dark:bg-[#161616] [&_.ant-modal-header]:dark:bg-[#161616] [&_.ant-modal-header]:dark:border-gray-800 [&_.ant-modal-title]:dark:text-white [&_.ant-modal-title]:blog-title [&_.ant-modal-title]:text-2xl [&_.ant-modal-close]:dark:text-gray-400 [&_.ant-modal-close:hover]:dark:text-white [&_.ant-modal-footer]:dark:bg-[#161616] [&_.ant-modal-footer]:dark:border-gray-800"
        title="Edit Learning Path"
        open={!!editingPath}
        onCancel={() => setEditingPath(null)}
        footer={null}
        width={800}
      >
        {editingPath && (
          <LearningPathEdit
            id={editingPath.id}
            onSave={() => setEditingPath(null)}
            onCancel={() => setEditingPath(null)}
          />
        )}
      </Modal>
    </div>
  );
};

// Main Learning Path Manager Component
export default function LearningPathManager() {
  const { id, action } = useParams();
  const navigate = useNavigate();

  if (action === 'edit') {
    return (
      <LearningPathEdit
        id={id}
        onSave={() => navigate(`/community/learning/${id}`)}
        onCancel={() => navigate(-1)}
      />
    );
  }

  if (id) {
    return <LearningPathView id={id} />;
  }

  return <LearningPathList />;
}